// Соответствие шагов сделки (stepsList) и контента read-only сводки прошедшего шага.
// `id` шага — стабильный PK типа шага на бэке (bargain_steps.id), не зависит от типа сделки/роли,
// поэтому маппим именно по нему (а не по num/локализованному name). Источник правды по id см.
// order-service BargainStage/bargain_steps.
import type { BargainDocument, BargainStep } from '@/types/deals';

export type DealStepKey =
  | 'spec' // 1 — Подпись спецификации
  | 'invoices' // 2 — Счета за товар и логистику
  | 'consignee' // 3 — Выбор грузополучателя
  | 'forwarderAssign' // 13 — Назначение экспедитора
  | 'forwarderInstruction' // 14 — Поручение экспедитора
  | 'carrierAssign' // 15 — Назначение перевозчика
  | 'forwarding' // 4 — Перевозка
  | 'shipment' // 5 — Подтверждение отгрузки
  | 'finalPayment' // 6 — Окончательный расчёт
  | 'updTorg' // 7 — УПД и ТОРГ-12
  | 'serviceFees' // 8 — Оплата услуг сервиса и экспедитора
  | 'closing' // 9 — Завершение сделки
  | 'schedule' // 10 — Согласование дат отгрузки
  | 'declaration' // 11 — Загрузка декларации
  | 'attestation' // 12 — Аттестация
  | 'unknown';

const ID_TO_KEY: Readonly<Record<number, DealStepKey>> = {
  1: 'spec',
  2: 'invoices',
  3: 'consignee',
  4: 'forwarding',
  5: 'shipment',
  6: 'finalPayment',
  7: 'updTorg',
  8: 'serviceFees',
  9: 'closing',
  10: 'schedule',
  11: 'declaration',
  12: 'attestation',
  13: 'forwarderAssign',
  14: 'forwarderInstruction',
  15: 'carrierAssign',
};

/** Стабильный ключ типа шага по его id (или 'unknown' для незнакомых). */
export function dealStepKey(step: BargainStep): DealStepKey {
  return (step.id != null ? ID_TO_KEY[step.id] : undefined) ?? 'unknown';
}

/** Кликабельны только пройденные и текущий шаги (будущие — ещё не начаты). */
export function isStepNavigable(step: BargainStep): boolean {
  return step.hasDone === true || step.isCurrent === true;
}

type DocType = NonNullable<BargainDocument['documentType']>;

// Документы, относящиеся к каждому шагу (по documentType). Используется для блока «Документы шага».
// Экспортируется ради теста: он перебирает ВСЕ ключи карты, поэтому новый шаг не может проскочить
// мимо проверки на снятые типы документов.
export const STEP_DOC_TYPES: Readonly<Record<DealStepKey, readonly DocType[]>> = {
  attestation: ['SELLER_ATTESTATION_DOCS', 'BUYER_ATTESTATION_DOCS'],
  schedule: ['SPECIAL_TERMS'],
  // AGENCY_AGREEMENT снят (§1.8): это была агентская оферта стороннего экспедитора, приходившая из
  // port-transit-connector; контур выведен из эксплуатации, новых документов этого типа не появляется.
  // У ранее заключённых сделок файл остаётся в общем архиве документов сделки (DealDocuments).
  spec: ['SPECIFICATION_TEMPLATE', 'SIGNED_SPECIFICATION', 'AGENT_ASSIGNMENT'],
  declaration: ['DECLARATION', 'TEST_REPORT'],
  invoices: [
    'PREPAYMENT_INVOICE_TEMPLATE',
    'SIGNED_PREPAYMENT_INVOICE',
    'LOGISTIC_INVOICE_TEMPLATE',
    'SIGNED_LOGISTIC_INVOICE',
  ],
  shipment: [],
  // Своих документов у шага не осталось.
  // TRANSPORTATION_CONTRACT снят: договор перевозки скачивался с сайта SmartSeeds, контур выведен из
  // эксплуатации раньше port-transit, новых документов этого типа не появляется. У ранее заключённых
  // сделок файл остаётся в общем архиве документов сделки (DealDocuments) — на dev таких документов нет.
  // Письмо на отгрузку (SHIPPING_LETTER_*) исключено из агентской схемы: на этой стадии оно висело
  // преждевременно, экспедитор выбирается позже. Сам документ отключается флагом `shipment-letter.enabled`
  // в order-service, но включение флага НЕ вернёт письмо в этот шаг — типы придётся вернуть сюда явно,
  // сейчас портал их не упоминает нигде.
  consignee: [],
  // Назначение экспедитора (принятие заявки экспедитором) — платформенных документов у шага нет.
  forwarderAssign: [],
  // Поручение экспедитору — черновик и подписанная версия.
  forwarderInstruction: ['FORWARDER_ASSIGNMENT_TEMPLATE', 'SIGNED_FORWARDER_ASSIGNMENT'],
  // Подписанная перевозчиком «Заявка на перевозку»: приходит в сделку из trucking-service вместе с
  // событием назначения и видна агенту.
  carrierAssign: ['CARRIER_ACCEPT_REQUEST'],
  forwarding: [
    'FORWARDER_TTN',
    'FORWARDER_TRN',
    'REGISTER_OF_SHIPPED',
    'REGISTER_OF_ACCEPTED',
    'TRUNCATED_RTS',
    'DAILY_UPD_TEMPLATE',
    'SIGNED_DAILY_UPD',
    'DAILY_REF_FOR_GOODS_QUALITY',
    'FORWARDER_REPORT',
    'FORWARDER_RECEIPT',
  ],
  finalPayment: [
    'FINAL_REF',
    'UNDERPAYMENT_INVOICE_TEMPLATE',
    'SIGNED_UNDERPAYMENT_INVOICE',
    'REFUND_REQUIREMENT_TEMPLATE',
    'SIGNED_REFUND_REQUIREMENT',
    'UKD_TEMPLATE',
    'SIGNED_UKD',
    'FINAL_ADDITIONAL_REQUIREMENT_TEMPLATE',
    'SIGNED_FINAL_ADDITIONAL_REQUIREMENT',
  ],
  // Типы документов УПД/ТОРГ-12 удалены из контракта order-service как неиспользуемые
  // (BargainDocType, коммит «удалить неиспользуемые поля»). Сам шаг жизненного цикла
  // (WAIT_UPD_TORG12_*) сохранён, но документов этих типов в сделке не бывает — список пуст.
  updTorg: [],
  serviceFees: [
    'FORWARDER_FEE_INVOICE',
    'DELIVERY_FEE_INVOICE',
    'SERVICE_FEE_UPD',
    'SERVICE_FEE_INVOICE',
    'UPD_REWARD',
    'UPD_REINVOICING',
    'FORWARDER_FINAL_DOCS',
  ],
  closing: [],
  unknown: [],
};

/** Документы сделки, относящиеся к данному шагу (для read-only блока документов). */
export function documentsForStep(documents: readonly BargainDocument[] | undefined, key: DealStepKey): BargainDocument[] {
  if (!documents?.length) {
    return [];
  }
  const allowed = new Set<string>(STEP_DOC_TYPES[key]);
  if (allowed.size === 0) {
    return [];
  }
  return documents.filter((doc) => doc.documentType != null && allowed.has(doc.documentType));
}
